import { ColumnDefinition } from 'components/TableColumn/TableColumn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import { useCallAPI } from 'hooks/useCallAPI';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { camelCase } from 'lodash';
import omit from 'lodash/omit';
import {
  OutdoorDeviceMetricType,
  OutdoorDeviceReportTableRecord,
  ReportGran
} from 'core/report/ReportData';
import { DefaultReportManager } from 'core/report/ReportManager';
import { useCoreContext } from 'contexts/coreContext';
import { MOMENT_TIMEZONE } from 'core';
import moment from 'moment-timezone';
import { useOutdoorDeviceReportDataProvider } from './dataProviderFactory';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';

export const KEY_COLUMN = 'deliveryTargetName';

const MAX_QUERY_DAYS = 7;

export const hourLabelValueMap = {
  '0': '12:00.00 AM',
  '1': '1:00.00 AM',
  '2': '2:00.00 AM',
  '3': '3:00.00 AM',
  '4': '4:00.00 AM',
  '5': '5:00.00 AM',
  '6': '6:00.00 AM',
  '7': '7:00.00 AM',
  '8': '8:00.00 AM',
  '9': '9:00.00 AM',
  '10': '10:00.00 AM',
  '11': '11:00.00 AM',
  '12': '12:00.00 PM',
  '13': '1:00.00 PM',
  '14': '2:00.00 PM',
  '15': '3:00.00 PM',
  '16': '4:00.00 PM',
  '17': '5:00.00 PM',
  '18': '6:00.00 PM',
  '19': '7:00.00 PM',
  '20': '8:00.00 PM',
  '21': '9:00.00 PM',
  '22': '10:00.00 PM',
  '23': '11:00.00 PM'
};

const reportManager = new DefaultReportManager();

export const useOutdoorDeviceReportModel = (
  l1ObjectId: number,
  startDate: string,
  endDate: string,
  deliveryUnit?: DeliveryUnit,
  /**
   * 這個廣告群組所屬的銷售渠道是不是電子紙。
   *
   * 電子紙機台沒有人流統計，所以「資料類型」只留曝光數，預設也直接落在曝光數；
   * 其他戶外渠道維持原本人流數 / 曝光數兩個選項、預設人流數。
   */
  isEpaper = false
) => {
  const { loading, callAPIs } = useCallAPI();
  const core = useCoreContext();
  const localeMeta = core?.accountManager.localeMeta;
  const timezone = localeMeta?.timezone || '+08:00';

  const [metricType, setMetricType] = useState<OutdoorDeviceMetricType>(
    isEpaper
      ? OutdoorDeviceMetricType.IMPRES
      : OutdoorDeviceMetricType.E_PUBLISHER_IMPRES
  );
  const metricTypeOptions = useMemo(
    () =>
      createSelectOptionsFromEnum(
        OutdoorDeviceMetricType,
        'outdoorDeviceMetric.labels.',
        isEpaper ? [OutdoorDeviceMetricType.E_PUBLISHER_IMPRES] : undefined,
        camelCase
      ),
    [isEpaper]
  );
  const [gran, setGran] = useState<ReportGran>(ReportGran.DAY);
  const granOptions = useMemo(
    () => [
      {
        value: ReportGran.DAY,
        label: i18n.t('outdoorDeviceGran.labels.day')
      },
      {
        value: ReportGran.HOUR,
        label: i18n.t('outdoorDeviceGran.labels.hour')
      }
    ],
    []
  );
  const [from, setFrom] = useState<string>(
    moment(startDate).startOf(gran).format('YYYY-MM-DD HH:mm:ss')
  );
  const [to, setTo] = useState<string>(
    moment(endDate).endOf(gran).format('YYYY-MM-DD HH:mm:ss')
  );

  const dayRangeError = useMemo(() => {
    if (gran !== ReportGran.HOUR) {
      return undefined;
    }
    const fromMoment = moment(from);
    const toMoment = moment(to);
    const diffDays = toMoment.diff(fromMoment, 'days') + 1;
    if (diffDays > MAX_QUERY_DAYS) {
      return i18n.t('outdoorDeviceReport.errors.maxQueryHours', {
        days: MAX_QUERY_DAYS,
        hours: MAX_QUERY_DAYS * 24
      });
    }
    return undefined;
  }, [from, to, gran]);

  const [reportTableData, setReportTableData] = useState<
    OutdoorDeviceReportTableRecord[]
  >([]);
  const [tableSummaryRecord, setTableSummaryRecord] =
    useState<OutdoorDeviceReportTableRecord>();
  // query options 是最後拿來 call api 的參數
  // 其他的 state 只是拿來做 UI 呈現，改變不會觸發資料重新載入
  const [queryOptions, setQueryOptions] = useState({
    from: startDate,
    to: endDate,
    metricType,
    gran
  });

  const onMetricTypeChange = useCallback((value: string | number) => {
    setMetricType(value as OutdoorDeviceMetricType);
  }, []);

  const onGranChange = useCallback((value: string | number) => {
    setGran(value as ReportGran);
  }, []);

  const onDayRangeChange = useCallback(
    (from = '', to = '') => {
      setFrom(moment(from).startOf(gran).format('YYYY-MM-DD HH:mm:ss'));
      setTo(moment(to).endOf(gran).format('YYYY-MM-DD HH:mm:ss'));
    },
    [gran]
  );

  const dataProvider = useOutdoorDeviceReportDataProvider(
    queryOptions.gran,
    deliveryUnit
  );

  const chartData = useMemo(() => {
    return dataProvider.getChartData(
      tableSummaryRecord,
      queryOptions.metricType
    );
  }, [dataProvider, tableSummaryRecord, queryOptions.metricType]);

  const columns: ColumnDefinition[] = useMemo(() => {
    // DayDataProvider 只吃一個參數，HourDataProvider 吃三個
    return dataProvider.getColumns(
      tableSummaryRecord,
      queryOptions.from,
      queryOptions.to
    );
  }, [dataProvider, tableSummaryRecord, queryOptions.from, queryOptions.to]);

  const metricTotalSum = useMemo(() => {
    if (!tableSummaryRecord) {
      return 0;
    }
    return Object.keys(tableSummaryRecord).reduce((sum, key) => {
      if (key !== KEY_COLUMN) {
        return sum + +tableSummaryRecord[key];
      }
      return sum;
    }, 0);
  }, [tableSummaryRecord]);

  const fetchReportData = useCallback(() => {
    const getDateWithTimezone = (time: string) => {
      const momentTimezone = MOMENT_TIMEZONE[timezone]
        ? MOMENT_TIMEZONE[timezone]
        : MOMENT_TIMEZONE['+08:00'];
      return moment.tz(time, momentTimezone).format();
    };
    const { from, to, metricType, gran } = queryOptions;
    callAPIs(
      [
        () =>
          reportManager.getOutdoorDeviceReportData(
            l1ObjectId,
            getDateWithTimezone(from),
            getDateWithTimezone(to),
            metricType,
            gran
          )
      ],
      (data) => {
        const normalizedRecords = data.records.map((record) => ({
          [KEY_COLUMN]: record.deliveryTargetName,
          ...dataProvider.normalizeData(
            omit(record, KEY_COLUMN),
            queryOptions.from,
            queryOptions.to
          )
        }));
        setReportTableData(normalizedRecords);

        const summaryRecord: OutdoorDeviceReportTableRecord =
          dataProvider.getSummaryRecord(
            data.records,
            queryOptions.from,
            queryOptions.to
          );
        setTableSummaryRecord(summaryRecord);
      }
    );
  }, [l1ObjectId, queryOptions, timezone, callAPIs, dataProvider]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const onQueryBtnClick = useCallback(() => {
    setQueryOptions({
      from,
      to,
      metricType,
      gran
    });
    // 清空 table data
    // 避免因為舊資料拿不到對應的 metric 資料造成圖表呈現異常
    setTableSummaryRecord(undefined);
    setReportTableData([]);
  }, [from, to, metricType, gran]);

  return {
    loading,
    metricType,
    metricTypeOptions,
    gran,
    granOptions,
    from,
    to,
    columns,
    reportTableData,
    metricTotalSum,
    tableSummaryRecord,
    queryOptions,
    chartData,
    dayRangeError,
    onMetricTypeChange,
    onGranChange,
    onDayRangeChange,
    onQueryBtnClick
  };
};
